import { AbortablePromise } from '~/utilities/promises'

import { sanitisePollHost } from '../apiUtils'
import type { ServiceDict } from '../externals'
import { HostUrl } from '../toolbox'

export type PollResponseStatus = { status: 'ok'; seq: number } | { status: 'aborted' | 'error' }

/**
 * PollService handles long-polling for job change notifications.
 *
 * Note: PollService does not extend APIService because:
 * - It doesn't require authentication (no auth token needed)
 * - It uses a different URL pattern (no /st/4 prefix)
 *
 * Constructor follows a consistent pattern with other services,
 * accepting ServiceDict but only using the pollHost.
 */
export class PollService {
    private hostUrl: HostUrl

    constructor(services: ServiceDict) {
        this.hostUrl = new HostUrl(sanitisePollHost(services.pollHost))
    }

    public pollForChanges(
        jobID: JobID,
        lastKnownSeq: number,
    ): AbortablePromise<PollResponseStatus> {
        return new AbortablePromise((signal) => {
            const request = new XMLHttpRequest()
            let canCancel = true

            signal.addEventListener('abort', () => {
                if (canCancel) {
                    request.abort()
                }
            })

            return new Promise<PollResponseStatus>((ok) => {
                const fail = () => {
                    canCancel = false
                    ok({ status: 'error' })
                }
                const abort = () => {
                    canCancel = false
                    ok({ status: 'aborted' })
                }
                const succeed = (seq: number) => {
                    canCancel = false
                    ok({ status: 'ok', seq })
                }

                request.addEventListener('load', () => {
                    if (request.status === 200) {
                        succeed(parseInt(request.responseText.split('\n')[0], 10) || 0)
                    } else {
                        fail()
                    }
                })
                request.addEventListener('error', fail)
                request.addEventListener('abort', abort)

                request.open(
                    'get',
                    this.hostUrl.getPath('/', {
                        seq: lastKnownSeq,
                        event: 'job_changed:' + jobID,
                    }),
                )
                request.send()
            })
        })
    }
}
